import { useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface ActivityLogEntry {
  id: string;
  business_id: string;
  activity_type: string;
  description: string;
  entity_type: string;
  entity_id: string;
  logged_by: string;
  created_at: string;
  user_name?: string;
}

export interface ActivityFilters {
  activityType: string | null;
  entityType: string | null;
  userId: string | null;
}

const PAGE_SIZE = 25;

export function useActivityLog(businessId: string | undefined) {
  const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<ActivityFilters>({ activityType: null, entityType: null, userId: null });
  const mountedRef = useRef(true);

  const fetchPage = useCallback(async (pageNum: number, reset: boolean = false) => {
    if (!businessId) return;
    setLoading(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      let query = supabase
        .from('activity_log')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false })
        .range(pageNum * PAGE_SIZE, (pageNum + 1) * PAGE_SIZE - 1)
        .abortSignal(controller.signal);

      if (filters.activityType) query = query.eq('activity_type', filters.activityType);
      if (filters.entityType) query = query.eq('entity_type', filters.entityType);
      if (filters.userId) query = query.eq('logged_by', filters.userId);

      const { data, error: err } = await query;
      if (err) throw err;
      const rows = (data ?? []) as ActivityLogEntry[];

      if (mountedRef.current) {
        setEntries(prev => reset ? rows : [...prev, ...rows]);
        setHasMore(rows.length === PAGE_SIZE);
        setPage(pageNum);
      }
    } catch {
      // silently fail
    } finally {
      clearTimeout(timeout);
      if (mountedRef.current) setLoading(false);
    }
  }, [businessId, filters]);

  const loadFirst = useCallback(() => { fetchPage(0, true); }, [fetchPage]);
  const loadMore = useCallback(() => { if (hasMore && !loading) fetchPage(page + 1); }, [fetchPage, hasMore, loading, page]);

  const updateFilters = useCallback((f: Partial<ActivityFilters>) => {
    setFilters(prev => ({ ...prev, ...f }));
    setEntries([]);
    setPage(0);
    setHasMore(true);
  }, []);

  return { entries, loading, hasMore, loadFirst, loadMore, filters, updateFilters };
}
